import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import {
  FaLink,
  FaPlus,
  FaTimes,
  FaSpinner
} from 'react-icons/fa';
import { LoadingSpinner } from './LoadingSpinner';

import {
  addAssetToProjectItem
} from '../api/projects';
import { getProjectAssets, ProjectAsset } from '../api/assets';
import { useProjectItemsAssetsLazy } from '../hooks/useProjectItemsAssetsLazy';

interface TaskAssetAttachmentProps {
  projectId: string;
  itemId: string;
  onUpdate?: () => void;
}

export const TaskAssetAttachment: React.FC<TaskAssetAttachmentProps> = ({
  projectId,
  itemId,
  onUpdate
}) => {
  const { keycloak } = useKeycloak();
  const [isLinking, setIsLinking] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState('');

  // Load linked assets using lazy-loaded hook (on-demand only)
  // fetchAssets can be called when user needs to see linked assets
  const { assets: linkedAssets } = useProjectItemsAssetsLazy(projectId, itemId);

  // Asset data state with loading indicator
  const [availableAssets, setAvailableAssets] = useState<ProjectAsset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // Cache with TTL (15 minutes)
  const CACHE_TTL = 15 * 60 * 1000;
  const assetsCacheRef = new Map<string, { data: ProjectAsset[]; timestamp: number }>();

  // Load available assets for linking with cache
  const loadAvailableAssets = async () => {
    if (!keycloak.token) return;

    const cacheKey = projectId;
    const cachedData = assetsCacheRef.get(cacheKey);
    const now = Date.now();

    // Check if cache exists and is not expired
    if (cachedData && (now - cachedData.timestamp) < CACHE_TTL) {
      setAvailableAssets(cachedData.data);
      return;
    }

    // Cache miss or expired - fetch from API
    setIsLoadingAssets(true);
    try {
      const assetsResponse = await getProjectAssets(keycloak.token, projectId);
      // Filter out already linked assets
      const available = assetsResponse.results.filter((asset: ProjectAsset) =>
        !linkedAssets.some(linked => linked.id === asset.id)
      );

      // Store in cache
      assetsCacheRef.set(cacheKey, { data: available, timestamp: now });
      setAvailableAssets(available);
    } catch (error) {
      console.error('Error loading available assets:', error);
      toast.error('Failed to load available assets');
    } finally {
      setIsLoadingAssets(false);
    }
  };

  // Handle asset selection
  const handleAssetSelect = (assetId: string) => {
    setSelectedAssetId(assetId);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAssetId) {
      toast.error('Please select an asset to link');
      return;
    }

    setIsLinking(true);
    try {
      await addAssetToProjectItem(keycloak.token || '', projectId, itemId, selectedAssetId);
      toast.success('Asset linked successfully');
      
      // Assets will be reloaded via the hook when the component remounts or itemId changes
      
      // Reset form
      setSelectedAssetId('');
      setShowModal(false);
      
      // Trigger parent update
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error: any) {
      console.error('Error linking asset:', error);
      
      if (error.message && error.message.includes('already linked')) {
        toast.warning('Asset is already linked to this item');
      } else {
        toast.error('Failed to link asset');
      }
    } finally {
      setIsLinking(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      loadAvailableAssets();
    }
  }, [showModal, linkedAssets]);

  const content = (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
        <div className="flex items-center space-x-3">
          <FaLink className="w-5 h-5 text-[#888]" />
          <h2 className="text-xl font-semibold font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">
            Link Assets
          </h2>
        </div>
        <button
          onClick={() => setShowModal(false)}
          className="p-2 hover:bg-[#fafafa] rounded-md transition-colors"
          disabled={isLinking}
        >
          <FaTimes className="w-4 h-4 text-[#888]" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Asset Selection */}
        <div>
          <label className="block text-sm font-medium text-[#444] mb-4 font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">
            Select Asset *
          </label>
          {isLoadingAssets ? (
            <LoadingSpinner text="Loading assets..." className="py-8" />
          ) : availableAssets.length === 0 ? (
            <div className="text-center py-8 text-[#888]">
              <FaLink className="w-12 h-12 mx-auto mb-4 text-[#ccc]" />
              <p>No available assets to link</p>
              <p className="text-sm">All assets are already linked to this item</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availableAssets.map((asset) => (
                <div key={asset.id} className="flex items-center justify-between p-4 border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-sm text-[#171717] font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">{asset.name}</div>
                    {asset.description && (
                      <div className="text-xs text-[#888] font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">{asset.description}</div>
                    )}
                  </div>
                  <input
                    type="radio"
                    name="selectedAssetId"
                    value={asset.id}
                    checked={selectedAssetId === asset.id}
                    onChange={(e) => handleAssetSelect(e.target.value)}
                    className="ml-3"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex space-x-3 pt-6">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="flex-1 px-4 py-2 text-sm font-medium text-[#444] bg-white border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] focus:outline-none focus:ring-0 focus:ring-indigo-500 font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]"
            disabled={isLinking}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#171717] border border-transparent rounded-lg hover:bg-[#333] focus:outline-none focus:ring-0 focus:ring-indigo-500 font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLinking || !selectedAssetId}
          >
            {isLinking ? (
              <div className="flex items-center justify-center">
                <FaSpinner className="w-4 h-4 animate-spin mr-2" />
                Linking...
              </div>
            ) : (
              'Link Asset'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Button */}
      {linkedAssets.length > 0 ? (
        // Assets exist - show count and actions
        <button
          onClick={() => setShowModal(true)}
          className="cursor-pointer flex items-center space-x-1 text-xs text-[#555] hover:text-[#171717] bg-[#fafafa] border border-[#eaeaea] rounded-lg px-2 py-1"
          title={`${linkedAssets.length} linked asset${linkedAssets.length !== 1 ? 's' : ''}`}
        >
          <FaLink className="text-xs" />
          <span className="font-medium">
            {linkedAssets.length}
          </span>
        </button>
      ) : (
        // No assets - show add button
        <button
          onClick={() => setShowModal(true)}
          disabled={isLinking}
          className="flex items-center space-x-1 bg-[#fafafa] hover:bg-[#fafafa] border border-[#eaeaea] rounded-lg px-3 py-1 text-xs text-[#888] hover:text-[#444] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Link assets"
        >
          {isLinking ? (
            <>
              <div className="animate-spin w-3 h-3 border border-[#bbb] border-t-gray-700 rounded-full"></div>
              <span>Linking...</span>
            </>
          ) : (
            <>
              <FaPlus className="text-xs" />
              <span>Link</span>
            </>
          )}
        </button>
      )}

      {/* Modal - Rendered via portal to avoid z-index issues with table */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 999999 }}>
          <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            {content}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}; 